import { Link, Navigate } from '@tanstack/react-router';
import { ErrorResponse } from 'oidc-client-ts';
import { useAuth } from 'react-oidc-context';
import { LOGIN_PATH } from '@/core/auth';
import { buttonClass, Card } from '@/core/ui';

/**
 * Retorno do Keycloak.
 *
 * A troca do `code` por tokens acontece no AuthProvider, antes do router
 * montar — e o `onSigninCallback` já leva o usuário à página de origem.
 * Esta tela só aparece se a troca falhou (state inválido, code expirado,
 * login social cancelado ou recusado pelo Keycloak).
 *
 * "Alterar senha" volta por aqui com `kc_action_status`: alterada ou
 * cancelada, o login se completa do mesmo jeito (ver AccountActionNotice).
 */
export function AuthCallbackPage() {
  const auth = useAuth();

  if (auth.isAuthenticated) return <Navigate to="/" replace />;
  // Acesso direto, sem `code` na URL: não há o que concluir.
  if (!auth.error) return <Navigate to={LOGIN_PATH} replace />;

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <Card className="w-full max-w-md p-8 text-center">
        <h1 className="text-lg font-semibold">Não foi possível concluir o login</h1>
        <p className="mt-2 text-sm text-fg-subtle">{callbackErrorMessage(auth.error)}</p>
        <Link to={LOGIN_PATH} className={`${buttonClass()} mt-6`}>
          Voltar para o login
        </Link>
      </Card>
    </div>
  );
}

/** O erro técnico (em inglês, do Keycloak ou do oidc-client-ts) vira uma frase para a tela. */
function callbackErrorMessage(error: Error & { innerError?: unknown }): string {
  const response =
    error instanceof ErrorResponse
      ? error
      : error.innerError instanceof ErrorResponse
        ? error.innerError
        : null;
  switch (response?.error) {
    case 'access_denied':
      return 'O login foi cancelado ou não foi autorizado.';
    case 'temporarily_unavailable':
    case 'server_error':
      return 'O servidor de autenticação está indisponível. Tente novamente em instantes.';
    case 'login_required':
    case 'invalid_grant':
      return 'Sua sessão expirou antes de concluir o login. Entre novamente.';
  }
  // Voltar com um `state` que esta aba não conhece: link antigo, aba
  // diferente, ou o login foi aberto em outro navegador (ex.: link do e-mail).
  if (/state/i.test(error.message))
    return 'Este link de login expirou ou foi aberto em outra aba. Entre novamente.';
  return 'Algo deu errado ao concluir o login. Tente novamente.';
}
